<template>
  <div
    ref="reviewIndex"
    class="main-review-list"
    :class="{ 'board-scrolled': detectDownScrollTrigger }"
  >
    <TopBarBackTitle
      :title="$t('texts.review')"
      :trigger="detectDownScrollTrigger"
      :show-back="!isWriteReview"
      :backUrl="backUrl"
    >
      <template #right>
        <span
          v-if="isWriteReview"
          @click="goToHistory"
          >구매정보가기</span
        >
      </template>
    </TopBarBackTitle>
    <client-only>
      <div class="main-review-list-body">
        <div
          v-for="review in reviews"
          :key="review.reviewId"
          class="reviews border-radius-8"
          :class="{ 'write-review': userData?.insideId === review.userInsideId && isWriteReview }"
        >
          <p class="title">
            {{ review.reviewPlan.planName }}
          </p>
          <p class="comment mt-default">
            {{ review.comment?.replace(/\n$/gm, '') }}
          </p>
          <p class="comment-writter mt-default">
            {{ review.userName }}
          </p>
          <el-rate
            v-model="review.rank"
            class="mt-default"
            :icons="icons"
            :void-icon="DeactivateStar"
            :disabled-void-icon="DeactivateStar"
            disabled
          />
        </div>
        <div ref="pullTrg" />
        <NotFound
          :not-found-trigger="!pending && !reviews?.length"
          :texts="$tm('review.notFoundReview')"
        />
      </div>
    </client-only>
    <el-backtop
      target=".main-review-list"
      :right="40"
      :bottom="175"
      :visibility-height="10"
    >
      <UpperArrow />
    </el-backtop>
  </div>
</template>

<script setup lang="ts">
import ActivateStar from '@/components/atom/icons/basic/ActivateStar.vue'
import DeactivateStar from '@/components/atom/icons/basic/DeactivateStar.vue'
import { navigateTo } from 'nuxt/app'
import type { IPlan } from '~/types/plan.interface'

const reviewIndex = ref<HTMLElement | null>(null)
const pullTrg = ref<HTMLElement | null>(null)

const { arrivedState, directions } = useScroll(reviewIndex, { behavior: 'smooth' })
const { top: toTop } = toRefs(directions)
const icons = [ActivateStar, ActivateStar, ActivateStar]

const { t } = useLocale()

const { userData } = storeToRefs(useUserData())
const { plans } = storeToRefs(usePlan())
const router = useRouter()
const { backUrl } = useRoute().query

definePageMeta({
  pageTransition: false,
  layoutTransition: true,
  layout: 'center',
  middleware: ['naver-ads', 'kakao-pixel']
})

const { ogUrl, ogTitle, keyword } = useSeo()

useSeoMeta({
  title: '리뷰',
  description:
    '일본, 대만, 아시아, 미국, 캐나다, 유럽 34개국 등 전세계에서 사용한 유심사 고객의 만족한 생생한 후기를 확인할 수 있어요!',
  ogType: 'website',
  ogUrl: () => ogUrl('reviews'),
  ogTitle: () => ogTitle('리뷰'),
  ogDescription:
    '일본, 대만, 아시아, 미국, 캐나다, 유럽 34개국 등 전세계에서 사용한 유심사 고객의 만족한 생생한 후기를 확인할 수 있어요!',
  twitterCard: 'summary',
  twitterTitle: () => ogTitle('사용방법'),
  twitterDescription:
    '일본, 대만, 아시아, 미국, 캐나다, 유럽 34개국 등 전세계에서 사용한 유심사 고객의 만족한 생생한 후기를 확인할 수 있어요!',
  keywords: keyword('리뷰, eSIM리뷰, 이심리뷰, USIM리뷰, 유심리뷰')
})

const searchResults = ref('')

const isWriteReview = computed(() => !!router.currentRoute.value.query.writeReview)
const detectDownScrollTrigger = computed(() => {
  return !(!toTop.value && arrivedState.top)
})

const {
  next,
  arr: reviews,
  clear,
  pending,
  searchParams
} = useStorableArrayFetcher<OrderReview>({
  requestUrl: 'reviews'
})

watch(
  () => router.currentRoute.value?.query?.planName,
  (val) => {
    try {
      if (typeof val !== 'string') {
        return
      }
      searchParams.plan = val
      clear()
    } catch (e) {
      console.warn(e)
    }
  },
  {
    immediate: true
  }
)

useIntersectionObserver(pullTrg, ([{ isIntersecting }], _) => {
  if (isIntersecting && !pending.value) {
    next()
  }
})

const querySearch = (queryString: string, callback: any) => {
  const items = plans.value
  if (!items) {
    return
  }
  const results = queryString ? items.filter(createFilter(queryString)) : items
  const disticts = new Set(results.map(({ planName }) => planName))
  callback([...disticts].map((value) => ({ value })))
}

const createFilter = (queryString: string) => {
  return (plan: IPlan.DayPlan) => {
    return plan.planName.toLowerCase().includes(queryString.toLowerCase())
  }
}

const searchCountry = (queryString: string) => {
  if (router.currentRoute.value?.query?.planName !== queryString) {
    navigateTo(router.currentRoute.value.path + '?planName=' + queryString, {
      replace: true
    })
  }
}

const goToHistory = () => {
  window.location.href = '/user/history'
}
</script>
